import axios from 'axios';
import { QueryFunction, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { Video, PaginatedResponse, SortOrder, TagsMatchMode } from '@repo/types';
import API from '../endpoints';
import { getAccessToken } from '../utils/getAccessToken';

// 2 minutes frontend cache for published videos
const CACHE_TIME = 2 * 60 * 1000;

export type PublishedVideosQueryArgs = {
  page?: number;
  limit?: number;
  tags?: string;
  tagsMatchMode?: TagsMatchMode;
  excludeTags?: string;
  videoChannelIds?: string;
  sortOrder?: SortOrder;
};

type QueryOptions = {
  enabled?: boolean;
};

export const usePublishedVideosQuery = (args?: PublishedVideosQueryArgs, options?: QueryOptions) => {
  return useQuery({
    queryKey: ['publishedVideos', args],
    queryFn: fetchPublishedVideos,
    staleTime: !getAccessToken() ? CACHE_TIME : undefined,
    ...(options ?? {}),
  });
};

const fetchPublishedVideos: QueryFunction<
  PaginatedResponse<Video>,
  [string, PublishedVideosQueryArgs | undefined]
> = async ({ queryKey }) => {
  const [, params] = queryKey;
  const accessToken = getAccessToken();

  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.tags) queryParams.set('tags', params.tags);
  if (params?.tagsMatchMode) queryParams.set('tagsMatchMode', params.tagsMatchMode);
  if (params?.excludeTags) queryParams.set('excludeTags', params.excludeTags);
  if (params?.videoChannelIds) queryParams.set('videoChannelIds', params.videoChannelIds);
  if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  const url = `${API.endpoints.videosPublished}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const { data } = await axios.get<PaginatedResponse<Video>>(url, {
    headers: accessToken
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : {},
  });
  return data;
};

export type InfinitePublishedVideosQueryArgs = Omit<PublishedVideosQueryArgs, 'page'>;

export const useInfinitePublishedVideosQuery = (args?: InfinitePublishedVideosQueryArgs, options?: QueryOptions) => {
  return useInfiniteQuery({
    queryKey: ['publishedVideos-infinite', args],
    queryFn: async ({ pageParam = 1 }) => {
      const accessToken = getAccessToken();

      const queryParams = new URLSearchParams();
      queryParams.set('page', pageParam.toString());
      if (args?.limit) queryParams.set('limit', args.limit.toString());
      if (args?.tags) queryParams.set('tags', args.tags);
      if (args?.tagsMatchMode) queryParams.set('tagsMatchMode', args.tagsMatchMode);
      if (args?.excludeTags) queryParams.set('excludeTags', args.excludeTags);
      if (args?.videoChannelIds) queryParams.set('videoChannelIds', args.videoChannelIds);
      if (args?.sortOrder) queryParams.set('sortOrder', args.sortOrder);

      const url = `${API.endpoints.videosPublished}?${queryParams.toString()}`;

      const { data } = await axios.get<PaginatedResponse<Video>>(url, {
        headers: accessToken
          ? {
              Authorization: `Bearer ${accessToken}`,
            }
          : {},
      });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.hasNextPage) {
        return lastPage.pagination.currentPage + 1;
      }
      return undefined;
    },
    staleTime: !getAccessToken() ? CACHE_TIME : undefined,
    ...(options ?? {}),
  });
};
